import React, { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import { Box, Button, Drawer, List } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { BusinessConfig, LogoPlacement } from '../../types';

interface NavbarProps {
  onOpenBooking: () => void;
  bookingEnabled?: boolean;
  bookingCtaLabel?: string;
  logoUrl: string | null;
  businessInfo: BusinessConfig['businessInfo'];
  logoPlacement: LogoPlacement;
  showServicesLink: boolean;
}

const Navbar: React.FC<NavbarProps> = ({
  onOpenBooking,
  bookingEnabled = true,
  bookingCtaLabel = 'Book Now',
  logoUrl,
  businessInfo,
  logoPlacement,
  showServicesLink,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggleDrawer = (open: boolean) => () => {
    setDrawerOpen(open);
  };

  const shouldShowLogo = logoPlacement !== 'heroOverlay' && !!logoUrl;
  const shouldShowBrandName = !shouldShowLogo && logoPlacement !== 'heroOverlay';

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          boxShadow: 0,
          backgroundColor: (theme) => theme.palette.primary.main,
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            padding: '3px 0'
          }}>
            {shouldShowLogo && logoUrl ? (
              <Box
                component="img"
                src={logoUrl}
                alt="logo"
                sx={{
                  display: 'block',
                  pt: '3px',
                  height: { xs: 40, sm: 48, md: 56, lg: 64 },
                  maxHeight: { xs: 48, sm: 56, md: 64, lg: 72 },
                  width: 'auto',
                  maxWidth: { xs: 200, sm: 300, md: 330, lg: 400 },
                  objectFit: 'contain',
                }}
              />
            ) : null}
            {shouldShowBrandName && (
              <Typography
                variant="h6"
                sx={{ color: (theme) => theme.palette.primary.contrastText }}
              >
                {businessInfo.name}
              </Typography>
            )}
          </Box>

          {/* Mobile view (hamburger) */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1 }}>
            <IconButton color="inherit" onClick={toggleDrawer(true)}>
              <MenuIcon sx={{ color: (theme) => theme.palette.primary.contrastText }} />
            </IconButton>
          </Box>

          {/* Desktop view buttons */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
            <a href="#hero">
              <Button sx={{ color: (theme) => theme.palette.primary.contrastText }}>Home</Button>
            </a>
            {showServicesLink && (
              <a href="#services">
                <Button sx={{ color: (theme) => theme.palette.primary.contrastText }}>Services</Button>
              </a>
            )}
            {bookingEnabled && (
              <Button sx={{ color: (theme) => theme.palette.primary.contrastText }} onClick={onOpenBooking}>
                {bookingCtaLabel}
              </Button>
            )}
            <a href="#contact">
              <Button sx={{ color: (theme) => theme.palette.primary.contrastText }}>Contact Us</Button>
            </a>
            <a href="/admin">
              <Button sx={{ color: (theme) => theme.palette.primary.contrastText }}>Login</Button>
            </a>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="top"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            width: '100%',
            position: 'fixed',
            top: '0',
            backgroundColor: (theme) => theme.palette.primary.main,
          },
        }}
      >
        <Box role="presentation" onClick={toggleDrawer(false)} onKeyDown={toggleDrawer(false)}>
          <List sx={{ padding: 0 }}>
            {showServicesLink && (
              <Button
                fullWidth
                onClick={() => (window.location.href = '#services')}
                sx={(theme) => ({
                  color: theme.palette.primary.contrastText,
                  textAlign: 'center',
                  justifyContent: 'center',
                  borderBottom: `1px solid ${alpha(theme.palette.primary.contrastText, 0.1)}`,
                  padding: '15px 0px',
                })}
              >
                Services
              </Button>
            )}
            {bookingEnabled && (
              <Button
                fullWidth
                onClick={onOpenBooking}
                sx={(theme) => ({
                  color: theme.palette.primary.contrastText,
                  textAlign: 'center',
                  justifyContent: 'center',
                  borderBottom: `1px solid ${alpha(theme.palette.primary.contrastText, 0.1)}`,
                  padding: '15px 0px',
                })}
              >
                {bookingCtaLabel}
              </Button>
            )}
            <Button
              fullWidth
              onClick={() => (window.location.href = '#contact')}
              sx={(theme) => ({
                color: theme.palette.primary.contrastText,
                textAlign: 'center',
                justifyContent: 'center',
                padding: '15px 0px',
              })}
            >
              Contact
            </Button>
            <Button
              fullWidth
              onClick={() => (window.location.href = '/admin')}
              sx={(theme) => ({
                color: theme.palette.primary.contrastText,
                textAlign: 'center',
                justifyContent: 'center',
                padding: '15px 0px',
                borderTop: `1px solid ${alpha(theme.palette.primary.contrastText, 0.1)}`,
              })}
            >
              Login
            </Button>
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
